import React, { useState, FC, useMemo } from 'react';
import { useFormik } from 'formik';
import { calculateQuantity } from 'src/components/BundleProduct/utils';
import {
    ArtworkPriceBreakProps,
    ContextType as BundleOptions,
} from 'src/components/BundleProduct/BundleOptionsProvider';
import { BundleProduct } from 'src/components/ProductDetail/utils/product-detail.types';
import { BundleInfo } from 'src/components/BundleProduct/BundleInfo';
import {
    organizeArtworkPriceInfo,
    sumArtworkPriceAndFee,
} from 'src/components/BundleProduct/ProductBundleSummary/helpers';
import { convertCustomisationToArtworkData } from 'src/components/BundleProduct/utils/convertCustomisationToArtworkData';
import { getCalculatedMediaEntries } from 'src/util/getCalculatedMediaEntries';
import { BundleConfig } from 'src/components/BundleProduct/types';
import { EMPTY_ARTWORK_ID } from 'src/constants/artwork';
import { SingleBundleSkeleton } from 'src/elements/Skeletons/Skeletons';
import { BundleCustomisationOptions } from 'src/components/BundleProduct/utils/BundleCustomisationOptions';
import camelcaseKeys from 'camelcase-keys';
import { CamelizedBundleVariants } from 'src/queries/__generated__/wweBundle';
import { YotpoReviews } from 'src/components/YotpoReviews/YotpoReviews';

import { Swatch } from '../ProductDetail/ProductSwatch';

import { Customisation } from './Customisation';
import { BundleSizesSelector } from './BundleSizesSelector';
import { MultipleStepCustomisation } from './MultipleStepCustomisation/MultipleStepCustomisation';
import { getValidationSchema } from './Bundle.schema';
import { FormData, ColorAndSize, Customisation as CustomisationType } from './types';
import { SubmitStep } from './SubmitStep';
import classes from './Bundle.scss';
import { BundleColorSection } from './BundleColorSection';
import { getCustomisationCost } from './utils';

type Props = Pick<BundleOptions, 'bundlePrice'> & {
    product: BundleProduct;
    onSubmit(values: FormData): void;
    vat: boolean;
    basketLoading: boolean;
    bundles: BundleConfig[];
    artworkPriceBreak?: ArtworkPriceBreakProps;
    maxCustomisations: number;
    customisationOption: string;
};

export const BundleForm: FC<Props> = ({
    product,
    onSubmit,
    basketLoading,
    bundles,
    bundlePrice,
    artworkPriceBreak,
    maxCustomisations,
    customisationOption,
}) => {
    const maxQuantity = bundles.reduce((sum, { maxQty }) => sum + maxQty, 0);

    const initialFormikValues = useMemo(
        () => ({
            bundles: bundles.map((bundle) => ({
                id: bundle.id,
                selectedColorsAndSizes: [],
            })),
            customisations: [],
        }),
        [bundles],
    );

    const formik = useFormik<FormData>({
        initialValues: initialFormikValues,
        validateOnMount: true,
        onSubmit: (values) => {
            onSubmit(values);
        },
        validationSchema: getValidationSchema(
            bundles,
            customisationOption === BundleCustomisationOptions.PlainOnly ||
                customisationOption === BundleCustomisationOptions.Optional,
        ),
    });

    const [openModal, setOpenModal] = useState(false);

    const handleBundleColorSet = async (swatch: Swatch, bundleIndex: number) => {
        const bundles = [...formik.values.bundles];

        const colorsAndSizes = bundles[bundleIndex].selectedColorsAndSizes;
        const colorAlreadySelected = colorsAndSizes.findIndex((bundle) => bundle.colorId === swatch.value_index) >= 0;
        if (colorAlreadySelected) {
            bundles[bundleIndex].selectedColorsAndSizes = colorsAndSizes.filter(
                (bundle) => bundle.colorId !== swatch.value_index,
            );

            await formik.setFieldValue('bundles', bundles);
        } else {
            bundles[bundleIndex].selectedColorsAndSizes.push({
                colorId: swatch.value_index,
                sizes: [],
            });
            await formik.setFieldValue('bundles', bundles);
        }

        return formik.validateForm();
    };

    const handleBundleUpdate = (bundle: ColorAndSize, bundleIndex: number) => {
        const bundles = [...formik.values.bundles];

        const index = bundles[bundleIndex].selectedColorsAndSizes.findIndex(
            (colorAndSize) => colorAndSize.colorId === bundle.colorId,
        );
        bundles[bundleIndex].selectedColorsAndSizes[index] = bundle;
        formik.setFieldValue('bundles', bundles);
    };

    const handleCreateCustomisation = (values: Partial<CustomisationType>) => {
        formik.setFieldValue('customisations', [...formik.values.customisations, values]);
    };

    const handleChangeCustomisation = (values: CustomisationType, index: number) => {
        const customisations = formik.values.customisations;
        if (customisations[index].selectedMethod !== values.selectedMethod) {
            customisations.forEach((customisation) => (customisation.selectedMethod = values.selectedMethod));
        }
        customisations[index] = values;
        formik.setFieldValue('customisations', customisations);
    };

    const handleDeleteCustomisation = (index: number) => {
        formik.setFieldValue(
            'customisations',
            formik.values.customisations.filter((c, i) => i !== index),
        );
    };

    const getCurrentQty = (index: number) => calculateQuantity(formik.values.bundles[index].selectedColorsAndSizes);
    const totalCurrentQty = formik.values.bundles.reduce(
        (a, { selectedColorsAndSizes }) => a + calculateQuantity(selectedColorsAndSizes),
        0,
    );

    const isSwatchDisabled = (colorId: number, bundleIndex: number) =>
        getCurrentQty(bundleIndex) === bundles[bundleIndex].maxQty &&
        !formik.values.bundles[bundleIndex].selectedColorsAndSizes.map((b) => b.colorId).includes(colorId);

    const artworksData = convertCustomisationToArtworkData(formik.values.customisations);
    const { artworkCost } = organizeArtworkPriceInfo({ artworkPriceBreak, artworksData, maxQty: maxQuantity });
    const { artworksPrice, artworksFee } = sumArtworkPriceAndFee(artworkCost);

    const allCustomisationsCost = artworksPrice * maxQuantity + artworksFee;
    const [firstCustomisation] = formik.values.customisations;

    return (
        <div>
            <BundleInfo
                product={product}
                bundlePrice={bundlePrice}
                sizeGuide={product.size_guides}
                mediaEntry={
                    bundles.length === 1
                        ? getCalculatedMediaEntries(
                              formik.values.bundles[0].selectedColorsAndSizes[
                                  formik.values.bundles[0].selectedColorsAndSizes.length - 1
                              ]?.colorId,
                              camelcaseKeys((bundles[0].bundleVariants ?? []) as readonly any[], {
                                  deep: true,
                              }) as CamelizedBundleVariants,
                          )
                        : undefined
                }
                bundles={bundles}
            />
            <form onSubmit={formik.handleSubmit}>
                {bundles.map((bundle, bundleIndex) =>
                    !('sizes' in bundle) ? (
                        <SingleBundleSkeleton key={bundleIndex} />
                    ) : (
                        <React.Fragment key={bundleIndex}>
                            <BundleColorSection
                                currentQty={getCurrentQty(bundleIndex)}
                                maxQty={bundle.maxQty}
                                swatches={bundle.swatches}
                                handleBundleColorSet={(swatch) => handleBundleColorSet(swatch, bundleIndex)}
                                isSwatchDisabled={(colorId) => isSwatchDisabled(colorId, bundleIndex)}
                                selectedSwatchesId={formik.values.bundles[bundleIndex].selectedColorsAndSizes?.map(
                                    (bundle) => bundle.colorId,
                                )}
                                bundleInformation={bundle.bundleInformation}
                                bundleIndex={bundleIndex}
                                details={bundle.details}
                                mediaEntry={
                                    bundles.length >= 1
                                        ? getCalculatedMediaEntries(
                                              formik.values.bundles[bundleIndex].selectedColorsAndSizes[
                                                  formik.values.bundles[bundleIndex].selectedColorsAndSizes.length - 1
                                              ]?.colorId,
                                              camelcaseKeys((bundle.bundleVariants ?? []) as readonly any[], {
                                                  deep: true,
                                              }) as CamelizedBundleVariants,
                                          )
                                        : undefined
                                }
                                productUrl={product.url_key}
                            />
                            {formik.values.bundles[bundleIndex].selectedColorsAndSizes?.map((colorAndSize, index) => (
                                <BundleSizesSelector
                                    key={index}
                                    sizes={bundle.sizes}
                                    colorAndSize={colorAndSize}
                                    onSubmit={(values) => handleBundleUpdate(values, bundleIndex)}
                                    swatches={bundle.swatches}
                                    onDelete={(swatch) => handleBundleColorSet(swatch, bundleIndex)}
                                    variants={bundle.variants}
                                    sizeGuide={product.size_guides}
                                    mediaEntry={getCalculatedMediaEntries(
                                        colorAndSize.colorId,
                                        camelcaseKeys((bundle.bundleVariants ?? []) as readonly any[], {
                                            deep: true,
                                        }) as CamelizedBundleVariants,
                                    )}
                                    maxQty={bundle.maxQty}
                                    currentQty={getCurrentQty(bundleIndex)}
                                />
                            ))}
                        </React.Fragment>
                    ),
                )}
                {openModal && (
                    <MultipleStepCustomisation
                        locations={product.locations}
                        isOpenModal
                        closeModal={() => setOpenModal(false)}
                        onSubmitCustomisation={handleCreateCustomisation}
                        customisation={
                            firstCustomisation?.selectedMethod
                                ? {
                                      selectedMethod: firstCustomisation?.selectedMethod,
                                      selectedArtwork: EMPTY_ARTWORK_ID,
                                      selectedPositions: [
                                          product.locations.find(
                                              (location) =>
                                                  !firstCustomisation?.selectedPositions?.includes(location.name),
                                          )?.name || '',
                                      ],
                                  }
                                : undefined
                        }
                        alreadySelectedPositions={firstCustomisation?.selectedPositions}
                        freeCustomisation={formik.values.customisations.length === 0}
                        avoidSelectingPosition={formik.values.customisations.length !== 0}
                        recommendedApplication={product.recommended_application}
                    />
                )}
                {formik.values.customisations.length > 0 && <div className={classes.separator} />}
                {formik.values.customisations.map((customisation, index) => (
                    <Customisation
                        key={index}
                        maxQuantity={maxQuantity}
                        locations={product.locations}
                        recommendedApplication={product.recommended_application}
                        customisation={customisation}
                        onChangeCustomisation={(values: CustomisationType) => handleChangeCustomisation(values, index)}
                        onDeleteCustomisation={handleDeleteCustomisation}
                        index={index}
                        customisationCost={getCustomisationCost({
                            customisation,
                            artworkCosts: artworkCost,
                            artworkPriceBreak,
                            maxQty: maxQuantity,
                            index,
                        })}
                        alreadySelectedPositions={index > 0 ? firstCustomisation?.selectedPositions : undefined}
                    />
                ))}
                <SubmitStep
                    bundlePrice={bundlePrice}
                    allCustomisationsCost={allCustomisationsCost}
                    setOpenModal={setOpenModal}
                    customisationButtonText={
                        formik.values.customisations.length === 0 ? 'Add your FREE logo' : 'Add another logo'
                    }
                    formIsValid={formik.isValid}
                    customisationEnabled={
                        formik.values.customisations.length < maxCustomisations && totalCurrentQty === maxQuantity
                    }
                    basketLoading={basketLoading}
                    isBundlePlain={customisationOption === BundleCustomisationOptions.PlainOnly}
                />
            </form>
            <div className={classes.reviewsAndQa}>
                <YotpoReviews
                    urlKey={product.url_key || ''}
                    productId={product.id}
                    price={bundlePrice.price}
                    name={product.name}
                    image={product.small_image}
                    isBundle
                />
            </div>
        </div>
    );
};
